/**
 * Thin client-side helpers for My Life emotional-safety features.
 *
 * Sensitivity is authoritative from the server: each record's /records/list
 * payload carries { sensitive: bool, support_orgs: [...] } resolved server-side
 * from the admin-configured mapping on Document Types + Library Tags.
 *
 * This module exists only to preserve import compatibility and offer a
 * safe reader.
 */

/**
 * Read sensitivity directly from the record. Returns a shape compatible with
 * the original analyseRecordSensitivity() output so existing callers keep
 * working.
 */
export function analyseRecordSensitivity(record) {
  if (!record) return { sensitive: false, categories: [], orgs: [], label: null };
  const orgs = record.support_orgs || [];
  // Categories come through implicitly on each org row (o.category) — used
  // as a fallback only. The org linked to a tag is "who can help", a
  // separate concern from "what actually triggered this" — its category
  // taxonomy (domestic_violence, substance_misuse, ...) doesn't even cover
  // neglect/physical_abuse/emotional_abuse, so it was never going to give a
  // precise label for those.
  const categories = Array.from(new Set(orgs.map((o) => o.category).filter(Boolean)));

  // The specific concern/statutory tag(s) that actually fired — this is
  // the real answer to "what kind of content is this", so prefer it.
  const sensitiveTagNames = record.sensitive_tags || [];
  const tagLabel = sensitiveTagNames
    .map((t) => record.library_tags_display?.[t])
    .find(Boolean);

  const label = tagLabel || labelForCategory(
    categories.find((c) => c !== 'general_listening' && c !== 'care_specific')
    ?? categories[0],
  );
  return {
    sensitive: !!record.sensitive,
    categories,
    orgs,
    label,
  };
}

const CATEGORY_LABELS = {
  sexual_abuse: 'Sexual abuse',
  domestic_violence: 'Domestic violence',
  suicide_selfharm: 'Self-harm or suicide',
  substance_misuse: 'Substance misuse',
  bereavement: 'Bereavement',
  mental_health: 'Mental health',
  criminal_justice: 'Criminal justice',
  advocacy_legal: 'Legal matters',
  care_specific: 'Care-related content',
  general_listening: 'Sensitive content',
  other: 'Sensitive content',
};

export function labelForCategory(c) {
  if (!c) return 'Sensitive content';
  return CATEGORY_LABELS[c] || 'Sensitive content';
}

/**
 * Preserved for backwards compat — callers that passed explicit category
 * keys previously can still resolve those to the server orgs array they
 * already have. In practice, pass record.support_orgs directly.
 */
export function supportOrgsFor(categoriesOrOrgs) {
  if (!categoriesOrOrgs) return [];
  if (Array.isArray(categoriesOrOrgs) && categoriesOrOrgs[0]?.id) {
    return categoriesOrOrgs; // already orgs
  }
  return [];
}
